import LoadingScreen from 'components/LoadingScreen';
import useSettings from 'hooks/useSettings';
import DashboardLayoutV1 from 'layouts/layout-v1/DashboardLayout';
import LayoutV2 from 'layouts/layout-v2/LayoutV2';
import DashboardLayoutV3 from 'layouts/layout-v3/DashboardLayout';
import CompleteEmployeeProfilePage from 'pages/employees/data-request/data-compiled-by-employee';
import PreContractSignPage from 'pages/pre-contract/PreContractSignPage';
import EmployeesTable from 'pages/employees/employees-table';
import CvsTable from 'pages/cvs/cvs-table';
import AllEmployeesTable from 'pages/employees/all-employees-table';
import EmployeesReport from 'pages/employees/report';
import NoAccessPage from 'pages/noAcessPage';
import SegmentTable from 'pages/segments/segment-table';
import SelectTenantPage from 'pages/selectTenantPage';
import ShiftAssignment from 'pages/shifts/shift-assignment';
import ShiftAssignmentV2 from 'pages/shifts/shift-assignment-v2';
import ShiftsCalendar from 'pages/shifts/shifts-calendar';
import ShiftsTable from 'pages/shifts/shifts-table';
import { type FC, lazy, type LazyExoticComponent, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { checkTokenRole } from 'utils/checkToken';
import EmployeeCommunications from 'pages/employees/communications';
import DefaultUserPage from 'pages/defaultUserPage';
import UniqueEmployee from 'pages/employees/unique-employee';
import Sections from 'pages/turnover/sections';
import TurnoverSummary from 'pages/turnover/turnoverSummary';
import PaymentMethods from 'pages/turnover/paymentMethods';
import UniqueEmployeeCalendar from 'pages/employees/unique-employee-calendar';
import ShiftRequests from 'pages/shifts/shift-requests';
import TurnoverStatistics from 'pages/turnover/turnoverStatistics';
import PublicDocuments from 'pages/public-documents/public-documents';
import ShiftsStatisticsDashboard from 'pages/shifts/shifts-statistics-dashboard';

const Loadable = (Component: LazyExoticComponent<FC>) => (props: any) => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

const TurnoverComparativePage = Loadable(
  lazy(async () => await import('./pages/turnover/TurnoverComparativePage')),
);

const JobDetailsPage = Loadable(
  lazy(async () => await import('./pages/job-advertisements/job-details-page')),
);

const DashboardMainPage = Loadable(
  lazy(async () => await import('./pages/dashboards/dashboard-main-page')),
);

const Account = Loadable(
  lazy(async () => await import('./pages/accounts/account')),
);

const Company = Loadable(
  lazy(async () => await import('./pages/company/company')),
);

const NewEmployee = Loadable(
  lazy(async () => await import('./pages/employees/new-employee')),
);
const EmployeeCalendar = Loadable(
  lazy(async () => await import('./pages/employees/calendar')),
);

const Timeline = Loadable(
  lazy(async () => await import('./pages/employees/timeline')),
);

const CvDetails = Loadable(lazy(async () => await import('./pages/cvs/cv')));

const DepartmentsTable = Loadable(
  lazy(async () => await import('./pages/departments/departments-table')),
);
const EmployeesHierarchy = Loadable(
  lazy(async () => await import('./pages/departments/employees-hierarchy')),
);
const ContractsTable = Loadable(
  lazy(async () => await import('./pages/data-table/contracts-table')),
);
const PositionsTable = Loadable(
  lazy(async () => await import('./pages/departments/workPositions-table')),
);
const ExperienceLevelsTable = Loadable(
  lazy(async () => await import('./pages/data-table/experienceLevels-table')),
);
const BuildingsTable = Loadable(
  lazy(async () => await import('./pages/buildings/buildings-table')),
);
const RoomsTable = Loadable(
  lazy(async () => await import('./pages/buildings/rooms-table')),
);
const RoomAssignmentsPage = Loadable(
  lazy(async () => await import('./pages/room-assignments/RoomAssignmentsPage')),
);
const SeasonsTable = Loadable(
  lazy(async () => await import('./pages/seasons/seasons-table')),
);

const JobAdvertisementsTable = Loadable(
  lazy(
    async () =>
      await import('./pages/job-advertisements/job-advertisements-table'),
  ),
);

const EventList = Loadable(
  lazy(async () => await import('./pages/dashboards/event-list')),
);
// authentication
const Login = Loadable(
  lazy(async () => await import('./pages/authentication/login')),
);
const Register = Loadable(
  lazy(async () => await import('./pages/authentication/register')),
);
const NewPassword = Loadable(
  lazy(async () => await import('./pages/authentication/new-password')),
);
const ForgetPassword = Loadable(
  lazy(async () => await import('./pages/authentication/forget-password')),
);
const ForgetPasswordV2 = Loadable(
  lazy(async () => await import('./pages/authentication/forget-password-v2')),
);
const ResetPassword = Loadable(
  lazy(async () => await import('./pages/authentication/reset-password')),
);
const ConfirmProfile = Loadable(
  lazy(async () => await import('./pages/authentication/confirm-user')),
);
const AfterRegister = Loadable(
  lazy(async () => await import('./pages/authentication/after-register')),
);
const EmployeeHoursTable = Loadable(
  lazy(async () => await import('./pages/shifts/EmployeeHoursTable')),
);

const AboutPage = Loadable(lazy(async () => await import('./pages/about')));
const ContactPage = Loadable(lazy(async () => await import('./pages/contact')));
const PricingPage = Loadable(
  lazy(async () => await import('./pages/pricing-v2')),
);
const PrivacyPage = Loadable(lazy(async () => await import('./pages/privacy')));

const PresenzePage = Loadable(
  lazy(async () => await import('./pages/time-attendance/presenze')),
);

const OvertimeApprovalsPage = Loadable(
  lazy(async () => await import('./pages/time-attendance/OvertimeApprovalsPage')),
);

const ReportsPage = Loadable(
  lazy(async () => await import('./pages/time-attendance/reports/ReportsPage')),
);

// 404/Error page
const ErrorPage = Loadable(lazy(async () => await import('./pages/404')));

// Coming Soon page
// const ComingSoonOverlay = Loadable(lazy(async () => await import('./pages/comingSoon')));

const ActiveLayout = () => {
  const { settings } = useSettings();

  switch (settings.activeLayout) {
    case 'layout1':
      return <DashboardLayoutV1 />;
    case 'layout2':
      return <LayoutV2 />;
    case 'layout3':
      return <DashboardLayoutV3 />;
    default:
      return <DashboardLayoutV1 />;
  }
};

export const routes = () => {
  return [
    ...authRoutes,
    ...jobsRoutes,
    {
      path: 'dashboard',
      element: (
        <div>
          <ActiveLayout />
          <Routes>
            <Route path='/' />
          </Routes>
        </div>
      ),
      children: dashboardRoutes,
    },
    {
      path: 'employee',
      element: (
        <div>
          <ActiveLayout />
          <Routes>
            <Route path='/' />
          </Routes>
        </div>
      ),
      children: employeesRoutes,
    },
    {
      path: '*',
      element: <ErrorPage />,
    },
  ];
};

let role;

try {
  role = checkTokenRole();
} catch (e) {}

const renderSwitch = (role: string | undefined) => {
  switch (role) {
    case 'DEPCHIEF': {
      return <Navigate to='/employee/defaultUserPage' />;
    }
    case 'USER': {
      return <Navigate to='/employee/defaultUserPage' />;
    }
    case 'CONTROLLER': {
      return <Navigate to='/dashboard/turnover/summary' />;
    }
    default:
      return <Navigate to='/dashboard' />;
  }
};

const OfferAcceptancePage = Loadable(
  lazy(async () => await import('./pages/public/OfferAcceptancePage')),
);

export const authRoutes = [
  { path: '/', element: renderSwitch(role) },

  { path: 'login', element: <Login /> },
  { path: 'register', element: <Register /> },
  { path: 'confirm-profile', element: <ConfirmProfile /> },
  { path: 'after-register', element: <AfterRegister /> },

  { path: 'new-password', element: <NewPassword /> },
  { path: 'forget-password', element: <ForgetPassword /> },
  { path: 'forget-password-v2', element: <ForgetPasswordV2 /> },
  { path: 'reset-password', element: <ResetPassword /> },
  { path: 'select-tenant', element: <SelectTenantPage /> },
  { path: 'no-access', element: <NoAccessPage /> },

  { path: 'about', element: <AboutPage /> },
  { path: 'contact', element: <ContactPage /> },
  { path: 'pricing', element: <PricingPage /> },
  { path: 'privacy', element: <PrivacyPage /> },

  { path: 'profile-completion', element: <CompleteEmployeeProfilePage /> },
  { path: 'pre-contract/sign', element: <PreContractSignPage /> },
  { path: 'jobs/:company/:id', element: <JobDetailsPage /> },
  { path: 'offers/:tenantId/:offerId', element: <OfferAcceptancePage /> },
];

const jobsRoutes = [{ path: 'jobs/:company/:id', element: <JobDetailsPage /> }];

const AreasTable = Loadable(
  lazy(async () => await import('./pages/building-safety/areas-table')),
);

const MetaAreasTable = Loadable(
  lazy(async () => await import('./pages/building-safety/meta-areas')),
);

const SafetySummary = Loadable(
  lazy(async () => await import('./pages/building-safety/safety-summary')),
);

const Floorplans = Loadable(
  lazy(async () => await import('./pages/building-safety/floorplans')),
);

const TodosPage = Loadable(
  lazy(async () => await import('./pages/todos/TodosPage')),
);

const TodoStatusConfigPage = Loadable(
  lazy(async () => await import('./pages/todos/TodoStatusConfigPage')),
);

const HiringPipelines = Loadable(
  lazy(async () => await import('./pages/recruitment/HiringPipelines')),
);

const TrainingSettingsPage = Loadable(
  lazy(async () => await import('./pages/training/TrainingSettingsPage')),
);

const TrainingComplianceSummary = Loadable(
  lazy(async () => await import('./pages/training/TrainingComplianceSummary')),
);

const TrainingCoursesPage = Loadable(
  lazy(async () => await import('./pages/training/TrainingCoursesPage')),
);

const ApplicationDetailsPage = Loadable(
  lazy(async () => await import('./pages/recruitment/ApplicationDetailsPage')),
);

const EmailTemplates = Loadable(
  lazy(async () => await import('./pages/recruitment/EmailTemplates')),
);

const RecruitmentDashboard = Loadable(
  lazy(async () => await import('./pages/recruitment/RecruitmentDashboard')),
);

const TalentPool = Loadable(
  lazy(async () => await import('./pages/recruitment/TalentPool')),
);

const JobAdStats = Loadable(
  lazy(async () => await import('./pages/recruitment/JobAdStats')),
);

const NewsletterDashboard = Loadable(
  lazy(async () => await import('./pages/newsletter/NewsletterDashboard')),
);
const NewsletterSettingsPage = Loadable(
  lazy(async () => await import('./pages/newsletter/NewsletterSettingsPage')),
);
const NewsletterDetailPage = Loadable(
  lazy(async () => await import('./pages/newsletter/NewsletterDetailPage')),
);
const EditionBuilderPage = Loadable(
  lazy(async () => await import('./pages/newsletter/EditionBuilderPage')),
);
const EditionReportPage = Loadable(
  lazy(async () => await import('./pages/newsletter/EditionReportPage')),
);

const dashboardRoutes = [
  { path: '', element: <DashboardMainPage /> },

  { path: 'account', element: <Account /> },
  { path: 'company', element: <Company /> },

  {
    path: 'employees/candidates',
    element: (
      <EmployeesTable isSeasonal={false} isAll={true} showCandidate={true} />
    ),
  },
  {
    path: '/dashboard/employees/',
    element: (
      <EmployeesTable isSeasonal={false} isAll={true} showCandidate={false} />
    ),
  },
  { path: 'employees/all', element: <AllEmployeesTable /> },
  {
    path: 'employees/annualemployees',
    element: (
      <EmployeesTable isSeasonal={false} isAll={false} showCandidate={false} />
    ),
  },
  {
    path: 'employees/seasonalemployees',
    element: (
      <EmployeesTable isSeasonal={true} isAll={false} showCandidate={false} />
    ),
  },
  { path: 'employees/new', element: <NewEmployee /> },
  {
    path: 'employees/calendar/:openRequestsModal',
    element: <EmployeeCalendar />,
  },
  { path: 'employees/report', element: <EmployeesReport /> },
  { path: 'employees/communications', element: <EmployeeCommunications /> },
  { path: 'employees/timeline', element: <Timeline /> },

  { path: 'cvs', element: <CvsTable /> },
  { path: 'cvs/cvDetails', element: <CvDetails /> },

  { path: '/dashboard/expiration', element: <EventList /> },
  { path: 'departments', element: <DepartmentsTable /> },
  { path: 'segments', element: <SegmentTable /> },
  { path: 'positions', element: <PositionsTable /> },
  { path: 'employees-hierarchy', element: <EmployeesHierarchy /> },
  { path: 'contracts', element: <ContractsTable /> },
  { path: 'experience-levels', element: <ExperienceLevelsTable /> },
  { path: 'buildings', element: <BuildingsTable /> },
  { path: 'rooms', element: <RoomsTable /> },
  { path: 'room-assignments', element: <RoomAssignmentsPage /> },
  { path: 'seasons', element: <SeasonsTable /> },
  { path: 'shift-template', element: <ShiftsTable /> },
  { path: 'shift/employeesHours', element: <EmployeeHoursTable /> },
  { path: 'shift/statistics', element: <ShiftsStatisticsDashboard /> },
  { path: 'shift-template/calendar', element: <ShiftsCalendar /> },
  { path: 'shift-assignments', element: <ShiftAssignment /> },
  { path: 'shift-assignments-v2', element: <ShiftAssignmentV2 /> },
  { path: 'job-advertisements', element: <JobAdvertisementsTable /> },
  { path: 'recruitment/pipelines', element: <HiringPipelines /> },
  { path: 'recruitment/application/:id', element: <ApplicationDetailsPage /> },
  { path: 'recruitment/email-templates', element: <EmailTemplates /> },
  { path: 'recruitment/dashboard', element: <RecruitmentDashboard /> },
  { path: 'recruitment/talent-pool', element: <TalentPool /> },
  { path: 'recruitment/job-ad-stats', element: <JobAdStats /> },
  { path: 'newsletter', element: <NewsletterDashboard /> },
  { path: 'newsletter/settings', element: <NewsletterSettingsPage /> },
  { path: 'newsletter/:id', element: <NewsletterDetailPage /> },
  { path: 'newsletter/:id/editions/:editionId', element: <EditionBuilderPage /> },
  { path: 'newsletter/:id/editions/:editionId/report', element: <EditionReportPage /> },
  { path: 'turnover/paymentMethods', element: <PaymentMethods /> },
  { path: 'turnover/sections', element: <Sections /> },
  { path: 'turnover/summary', element: <TurnoverSummary /> },
  { path: 'turnover/statistics', element: <TurnoverStatistics /> },
  { path: 'turnover/comparison', element: <TurnoverComparativePage /> },
  { path: 'public-documents', element: <PublicDocuments /> },
  { path: 'building-safety/summary', element: <SafetySummary /> },
  { path: 'building-safety/areas', element: <AreasTable /> },
  { path: 'building-safety/meta-areas', element: <MetaAreasTable /> },
  { path: 'building-safety/floorplans', element: <Floorplans /> },

  { path: 'todos', element: <TodosPage /> },
  { path: 'todos/config', element: <TodoStatusConfigPage /> },

  { path: 'settings/training', element: <TrainingSettingsPage /> },

  { path: 'training/summary', element: <TrainingComplianceSummary /> },
  { path: 'training/courses', element: <TrainingCoursesPage /> },

  { path: 'presenze', element: <PresenzePage /> },
  { path: 'presenze/approvals', element: <OvertimeApprovalsPage /> },
  { path: 'presenze/reports', element: <ReportsPage /> },
];

const employeesRoutes = [
  { path: '/employee/defaultUserPage', element: <DefaultUserPage /> },
  { path: '/employee/info', element: <UniqueEmployee /> },
  { path: '/employee/calendar', element: <UniqueEmployeeCalendar /> },
  { path: '/employees/chat', element: <></> },
  { path: '/employees/shifts', element: <></> },

  { path: '/employee/shiftrequests', element: <ShiftRequests /> },
  { path: '/employee/todos', element: <TodosPage /> },
];
